import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AppIcon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PersonalInfoTab from './components/PersonalInfoTab';
import SecurityTab from './components/SecurityTab';
import PreferencesTab from './components/PreferencesTab';
import NotificationsTab from './components/NotificationsTab';

const TABS = [
  {
    id: 'personal',
    label: 'Personal Info',
    icon: 'User',
    component: PersonalInfoTab,
  },
  {
    id: 'security',
    label: 'Security',
    icon: 'ShieldCheck',
    component: SecurityTab,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    icon: 'Settings',
    component: PreferencesTab,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: 'Bell',
    component: NotificationsTab,
  },
];

export default function ProfileSettings() {
  const [activeTab, setActiveTab] = useState('personal');
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const ActiveTabComponent = TABS.find((tab) => tab.id === activeTab)?.component;

  const handleGoBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGoBack}
                className="gap-2"
              >
                <AppIcon name="ArrowLeft" className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              
              <div className="border-l border-border h-8"></div>
              
              <div>
                <h1 className="text-2xl font-bold text-foreground">Profile Settings</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage your account settings and preferences
                </p>
              </div>
            </div>
            
            {/* User Info Badge */}
            <div className="flex items-center gap-3 bg-muted px-4 py-2 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
          {/* Tabs Navigation */}
          <div className="border-b border-border">
            <nav className="flex -mb-px" aria-label="Tabs">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      group inline-flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm
                      transition-colors duration-200
                      ${
                        isActive
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <AppIcon
                      name={tab.icon}
                      className={`w-5 h-5 ${
                        isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      }`}
                    />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {ActiveTabComponent && <ActiveTabComponent />}
          </div>
        </div>
      </div>
    </div>
  );
}
